import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose, MdOpenInNew } from 'react-icons/md';

import HttpStatus from '../../enums/httpStatus';
import { tenantActiveAiAgentApi } from '../../services/requests';
import type { AiAgent } from '../../types';
import checkPermission from '../../utils/checkPermission';
import * as s from './styles';

const AiAgentChatWidget = () => {
  const { t } = useTranslation();
  const [aiAgent, setAiAgent] = useState<AiAgent | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const canUseAiAgents = checkPermission('use-ai-agents');

  useEffect(() => {
    if (!canUseAiAgents) {
      return;
    }

    let isMounted = true;

    const loadActiveAiAgent = async () => {
      try {
        const response = await tenantActiveAiAgentApi.get();

        if (!isMounted || response.status !== HttpStatus.OK) {
          return;
        }

        setAiAgent(response.data.data ?? null);
      } catch (error) {
        if (isMounted) {
          setAiAgent(null);
        }
      }
    };

    loadActiveAiAgent();

    return () => {
      isMounted = false;
    };
  }, [canUseAiAgents]);

  if (!canUseAiAgents || !aiAgent?.active || !aiAgent.data?.url) {
    return null;
  }

  const shouldUseExternalAccess = aiAgent.type === 'OPENAI_CHAT_GPT';

  return (
    <>
      <s.WidgetPanel isOpen={isOpen}>
        <s.WidgetHeader>
          <s.WidgetTitle>{t('aiAgent')}</s.WidgetTitle>

          <s.WidgetActions>
            <s.IconButton
              type="button"
              title={t('openInNewTab')}
              onClick={() => window.open(aiAgent.data.url, '_blank', 'noopener,noreferrer')}
            >
              <MdOpenInNew />
            </s.IconButton>

            <s.IconButton
              type="button"
              title={t('closeAiAssistant')}
              onClick={() => setIsOpen(false)}
            >
              <MdClose />
            </s.IconButton>
          </s.WidgetActions>
        </s.WidgetHeader>

        {shouldUseExternalAccess ? (
          <s.ExternalOnlyContent>
            <s.ExternalOnlyIcon>
              <MdOpenInNew />
            </s.ExternalOnlyIcon>

            <s.ExternalOnlyTitle>
              {t('aiAgentExternalOnlyTitle')}
            </s.ExternalOnlyTitle>
            <s.ExternalOnlyDescription>
              {t('aiAgentExternalOnlyDescription')}
            </s.ExternalOnlyDescription>

            <s.PrimaryExternalLinkButton href={aiAgent.data.url} target="_blank" rel="noreferrer">
              {t('openInNewTab')}
            </s.PrimaryExternalLinkButton>
          </s.ExternalOnlyContent>
        ) : (
          <>
            <s.WidgetFrame
              src={aiAgent.data.url}
              title={t('aiAgent')}
              loading="lazy"
              allow="clipboard-read; clipboard-write; microphone"
              referrerPolicy="strict-origin-when-cross-origin"
            />

            <s.WidgetFooter>
              <s.ExternalLinkButton href={aiAgent.data.url} target="_blank" rel="noreferrer">
                {t('openInNewTab')}
              </s.ExternalLinkButton>
            </s.WidgetFooter>
          </>
        )}
      </s.WidgetPanel>

      <s.FloatingButton
        type="button"
        title={isOpen ? t('closeAiAssistant') : t('openAiAssistant')}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <s.FloatingButtonIcon src="/smartsell-ai.png" alt="" aria-hidden="true" />
      </s.FloatingButton>
    </>
  );
};

export default AiAgentChatWidget;